/**
 * Lectura de archivos Excel/CSV en el navegador.
 *
 * Devuelve dos vistas de las mismas filas:
 * - `rows`: valores formateados (texto), útiles para mostrar y validar.
 * - `rawRows`: valores crudos (números, fechas), para detectar cuándo una
 *   celda se guardó como número y evitar que Excel la convierta en notación
 *   científica o pierda ceros iniciales.
 */

export interface SpreadsheetData {
  headers: string[];
  rows: Record<string, unknown>[];
  rawRows: Record<string, unknown>[];
}

const toObjects = (headers: string[], matrix: unknown[][]): Record<string, unknown>[] =>
  matrix.map((row) => {
    const obj: Record<string, unknown> = {};
    headers.forEach((header, index) => {
      if (!header) return;
      obj[header] = row[index] ?? '';
    });
    return obj;
  });

export const readSpreadsheetFile = async (file: File): Promise<SpreadsheetData> => {
  const XLSX = await import('xlsx');
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: 'array', cellDates: true });

  const sheetName = workbook.SheetNames[0];
  if (!sheetName) throw new Error('El archivo no contiene hojas.');

  const sheet = workbook.Sheets[sheetName];
  const textMatrix = XLSX.utils.sheet_to_json<unknown[]>(sheet, {
    header: 1,
    defval: '',
    raw: false,
  });
  const rawMatrix = XLSX.utils.sheet_to_json<unknown[]>(sheet, {
    header: 1,
    defval: '',
    raw: true,
  });

  if (!textMatrix.length) throw new Error('El archivo está vacío.');

  const headers = textMatrix[0].map((h) => String(h ?? '').trim());
  const dataIndexes: number[] = [];
  textMatrix.slice(1).forEach((row, index) => {
    if (row.some((cell) => String(cell ?? '').trim() !== '')) dataIndexes.push(index + 1);
  });

  const rows = toObjects(
    headers,
    dataIndexes.map((index) => textMatrix[index])
  );
  const rawRows = toObjects(
    headers,
    dataIndexes.map((index) => rawMatrix[index] ?? [])
  );

  return { headers, rows, rawRows };
};
